import { COLORS, FONTS } from '../../theme/tokens';

const BODY_TEXT_SELECTOR = [
  '& p',
  '& p span',
  '& li',
  '& li span',
  '& th',
  '& th span',
  '& td',
  '& td span',
].join(', ');

const TOP_LEVEL_BACKGROUND_SELECTOR = [
  '& > div[style*="background"]',
  '& > article[style*="background"]',
  '& > section[style*="background"]',
].join(', ');

const HEADING_SELECTOR = [
  '& h2',
  '& h3',
  '& h4',
  '& h5',
  '& h6',
].join(', ');

const HEADING_CONTENT_SELECTOR = [
  '& h2 span',
  '& h3 span',
  '& h4 span',
  '& h5 span',
  '& h6 span',
].join(', ');

const BLOCKQUOTE_CONTENT_SELECTOR = [
  '& blockquote',
  '& blockquote p',
  '& blockquote p span',
].join(', ');

// Shared presentation layer for sanitized CMS HTML. Every selector stays scoped
// to the content container so legacy records can be normalized without changing
// CMS data or affecting product/blog/admin markup.
export const CMS_CONTENT_SX = {
  mb: 3,
  color: COLORS.textBody,
  fontFamily: FONTS.body,
  fontSize: { xs: '1rem', md: '1.05rem' },
  lineHeight: 1.8,
  overflowWrap: 'anywhere',
  wordBreak: 'normal',
  '& *, & *::before, & *::after': {
    boxSizing: 'border-box',
  },
  [TOP_LEVEL_BACKGROUND_SELECTOR]: {
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  [BODY_TEXT_SELECTOR]: {
    color: `${COLORS.textBody} !important`,
    fontFamily: `${FONTS.body} !important`,
    fontSize: 'inherit !important',
    lineHeight: 'inherit !important',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  '& p': {
    marginTop: '0 !important',
    marginBottom: '1.25rem !important',
  },
  '& ul, & ol': {
    marginTop: '0 !important',
    marginBottom: '1.5rem !important',
    paddingLeft: '1.5rem !important',
    color: `${COLORS.textBody} !important`,
    fontFamily: `${FONTS.body} !important`,
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  '& li': {
    marginBottom: '0.625rem !important',
  },
  [HEADING_SELECTOR]: {
    color: `${COLORS.textPrimary} !important`,
    fontFamily: `${FONTS.heading} !important`,
    fontWeight: '600 !important',
    lineHeight: '1.35 !important',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  [HEADING_CONTENT_SELECTOR]: {
    color: 'inherit !important',
    fontFamily: 'inherit !important',
    fontSize: 'inherit !important',
    fontWeight: 'inherit !important',
    lineHeight: 'inherit !important',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  '& h2': {
    marginTop: '2.75rem !important',
    marginBottom: '1rem !important',
    fontSize: { xs: '1.5rem !important', md: '1.8rem !important' },
  },
  '& h3': {
    marginTop: '2.25rem !important',
    marginBottom: '0.875rem !important',
    fontSize: { xs: '1.3rem !important', md: '1.5rem !important' },
  },
  '& h4': {
    marginTop: '2rem !important',
    marginBottom: '0.75rem !important',
    fontSize: { xs: '1.2rem !important', md: '1.35rem !important' },
  },
  '& h5, & h6': {
    marginTop: '1.75rem !important',
    marginBottom: '0.75rem !important',
    fontSize: { xs: '1.1rem !important', md: '1.2rem !important' },
  },
  '& a': {
    color: `${COLORS.green} !important`,
    textDecorationColor: COLORS.gold,
    textDecorationThickness: '1px',
    textUnderlineOffset: '3px',
    overflowWrap: 'anywhere',
    '&:hover': {
      color: `${COLORS.greenDark} !important`,
      textDecorationColor: COLORS.greenDark,
    },
  },
  [BLOCKQUOTE_CONTENT_SELECTOR]: {
    color: `${COLORS.textPrimary} !important`,
    fontFamily: `${FONTS.heading} !important`,
    fontSize: { xs: '1.2rem !important', md: '1.35rem !important' },
    lineHeight: '1.55 !important',
    background: 'transparent !important',
    backgroundColor: 'transparent !important',
  },
  '& blockquote': {
    margin: { xs: '2rem 0 !important', md: '2.5rem 0 !important' },
    padding: '0.5rem 0 0.5rem 1.5rem !important',
    borderLeft: `3px solid ${COLORS.gold}`,
  },
  '& blockquote p:last-child': {
    marginBottom: '0 !important',
  },
  '& img': {
    display: 'block',
    maxWidth: '100% !important',
    height: 'auto !important',
    margin: '2rem auto !important',
  },
  '& .row': {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(4, minmax(0, 1fr))',
    },
    gap: { xs: 1.5, sm: 2 },
    width: '100%',
    margin: { xs: '1.5rem 0', md: '2rem 0' },
  },
  '& .row > [class*="col-"]': {
    minWidth: 0,
    width: 'auto !important',
    maxWidth: 'none !important',
    margin: '0 !important',
    padding: '0 !important',
  },
  '& .text-center': {
    textAlign: 'center !important',
  },
  '& .row img, & img.img-fluid': {
    width: '100% !important',
    maxWidth: '100% !important',
    height: 'auto !important',
    margin: '0 !important',
  },
  '& table': {
    display: 'block',
    width: '100% !important',
    maxWidth: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    borderCollapse: 'collapse',
  },
  '& th, & td': {
    padding: '0.75rem',
    verticalAlign: 'top',
  },
  '& iframe, & video': {
    display: 'block',
    width: '100% !important',
    maxWidth: '100% !important',
    height: 'auto !important',
    aspectRatio: '16 / 9',
    margin: { xs: '1.5rem 0', md: '2rem 0' },
    border: 0,
  },
};
